import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { MapPin, Calendar as CalendarIcon, ArrowRight, X, Tag, Guitar, ArrowUpDown, Clock, List, Map as MapIcon } from "lucide-react";
import { format, startOfWeek, endOfWeek, addWeeks, startOfMonth, endOfMonth, isBefore, isAfter, isWithinInterval } from "date-fns";
import { es } from "date-fns/locale";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom red marker for events
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Gray marker for past events
const grayIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Barcelona default center
const DEFAULT_CENTER = [41.3851, 2.1734];
const DEFAULT_ZOOM = 13;

const EVENT_TAGS = [
  { id: "concierto", label: "CONCIERTO" },
  { id: "dj", label: "DJ" },
  { id: "vermut", label: "VERMUT" },
  { id: "afterwork", label: "AFTERWORK" },
  { id: "promo", label: "PROMO" },
  { id: "comida", label: "COMIDA" },
  { id: "afterparty", label: "AFTERPARTY" },
  { id: "festival", label: "FESTIVAL" },
  { id: "acustico", label: "ACÚSTICO" },
  { id: "jam-session", label: "JAM SESSION" },
  { id: "open-mic", label: "OPEN MIC" },
  { id: "tributo", label: "TRIBUTO" },
  { id: "release-party", label: "RELEASE PARTY" }
];

const MUSIC_GENRES = [
  { category: "Rock", genres: ["rock", "rock-clasico", "rock-alternativo", "rock-indie", "hard-rock", "rock-progresivo", "rock-psicodelico", "glam-rock"] },
  { category: "Punk", genres: ["punk", "punk-rock", "post-punk", "hardcore-punk", "pop-punk", "skate-punk", "street-punk", "crust-punk"] },
  { category: "Metal Clásico", genres: ["metal", "heavy-metal", "thrash-metal", "power-metal", "doom-metal", "speed-metal", "gothic-metal"] },
  { category: "Metal Moderno", genres: ["nu-metal", "metalcore", "progressive-metal", "symphonic-metal", "folk-metal", "industrial-metal", "groove-metal", "djent"] },
  { category: "Metal Extremo", genres: ["death-metal", "black-metal", "deathcore", "grindcore", "brutal-death", "melodic-death"] },
  { category: "Otros", genres: ["grunge", "stoner", "sludge", "post-rock", "noise-rock", "emo", "screamo", "shoegaze", "blues-rock"] }
];

const DATE_FILTERS = [
  { id: "upcoming", label: "PRÓXIMOS" },
  { id: "this-week", label: "ESTA SEMANA" },
  { id: "next-week", label: "PRÓXIMA SEMANA" },
  { id: "this-month", label: "ESTE MES" },
  { id: "past", label: "SOLO PASADOS" },
  { id: "all", label: "TODOS" }
];

export default function Events() {
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const selectedCity = searchParams.get("city") || "";
  const selectedTags = searchParams.get("tags")?.split(",").filter(Boolean) || [];
  const selectedGenres = searchParams.get("genres")?.split(",").filter(Boolean) || [];
  const selectedPeriod = searchParams.get("period") || "all"; // Default: all events
  const sortOrder = searchParams.get("sort") || "asc"; // Default: closest first
  const [showGenres, setShowGenres] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await axios.get(`${API}/cities`);
        setCities(res.data);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCity) params.city_id = selectedCity;
        if (selectedTags.length > 0) params.tags = selectedTags.join(",");
        if (selectedGenres.length > 0) params.genres = selectedGenres.join(",");
        
        const res = await axios.get(`${API}/events`, { params });
        let filteredEvents = res.data;
        
        // Filter by period
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        
        if (selectedPeriod === "upcoming") {
          // Only future events (from today onwards)
          filteredEvents = filteredEvents.filter(e => new Date(e.event_date) >= now);
        } else if (selectedPeriod === "this-week") {
          const weekStart = startOfWeek(now, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
          filteredEvents = filteredEvents.filter(e => {
            const eventDate = new Date(e.event_date);
            return isWithinInterval(eventDate, { start: weekStart, end: weekEnd });
          });
        } else if (selectedPeriod === "next-week") {
          const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
          const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
          filteredEvents = filteredEvents.filter(e => {
            const eventDate = new Date(e.event_date);
            return isWithinInterval(eventDate, { start: nextWeekStart, end: nextWeekEnd });
          });
        } else if (selectedPeriod === "this-month") {
          const monthStart = startOfMonth(now);
          const monthEnd = endOfMonth(now);
          filteredEvents = filteredEvents.filter(e => {
            const eventDate = new Date(e.event_date);
            return isWithinInterval(eventDate, { start: monthStart, end: monthEnd });
          });
        } else if (selectedPeriod === "past") {
          filteredEvents = filteredEvents.filter(e => new Date(e.event_date) < now);
        }
        // "all" shows everything without filtering
        
        // Sort by date
        filteredEvents.sort((a, b) => {
          const dateA = new Date(a.event_date);
          const dateB = new Date(b.event_date);
          
          if (selectedPeriod === "past") {
            return dateB - dateA; // Most recent past first
          }
          
          // For "all" view: show future events first (sorted by date), then past events
          if (selectedPeriod === "all") {
            const now = new Date();
            const aIsFuture = dateA >= now;
            const bIsFuture = dateB >= now;
            
            // Future events come first
            if (aIsFuture && !bIsFuture) return -1;
            if (!aIsFuture && bIsFuture) return 1;
            
            // Both future: closest first / Both past: most recent first
            if (aIsFuture && bIsFuture) {
              return dateA - dateB; // Closest future first
            } else {
              return dateB - dateA; // Most recent past first
            }
          }
          
          return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
        });
        
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [selectedCity, selectedTags.join(","), selectedGenres.join(","), selectedPeriod, sortOrder]);

  const handleCityChange = (value) => {
    if (value === "all") {
      searchParams.delete("city");
    } else {
      searchParams.set("city", value);
    }
    setSearchParams(searchParams);
  };

  const handlePeriodChange = (value) => {
    if (value === "upcoming") {
      searchParams.delete("period");
    } else {
      searchParams.set("period", value);
    }
    setSearchParams(searchParams);
  };

  const toggleSortOrder = () => {
    searchParams.set("sort", sortOrder === "asc" ? "desc" : "asc");
    setSearchParams(searchParams);
  };

  const toggleTag = (tagId) => {
    let newTags;
    if (selectedTags.includes(tagId)) {
      newTags = selectedTags.filter(t => t !== tagId);
    } else {
      newTags = [...selectedTags, tagId];
    }
    if (newTags.length > 0) {
      searchParams.set("tags", newTags.join(","));
    } else {
      searchParams.delete("tags");
    }
    setSearchParams(searchParams);
  };

  const toggleGenre = (genre) => {
    let newGenres;
    if (selectedGenres.includes(genre)) {
      newGenres = selectedGenres.filter(g => g !== genre);
    } else {
      newGenres = [...selectedGenres, genre];
    }
    if (newGenres.length > 0) {
      searchParams.set("genres", newGenres.join(","));
    } else {
      searchParams.delete("genres");
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setShowGenres(false);
  };

  const hasFilters = selectedCity || selectedTags.length > 0 || selectedGenres.length > 0 || selectedPeriod !== "all";

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-4">
            EVENTOS
          </h1>
          <p className="font-mono text-sm text-zinc-500">
            {events.length} evento{events.length !== 1 ? "s" : ""} 
            {selectedPeriod === "past" ? " pasados" : selectedPeriod === "upcoming" ? " próximos" : ""}
          </p>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* City Filter */}
          <Select value={selectedCity || "all"} onValueChange={handleCityChange}>
            <SelectTrigger className="w-[180px] bg-[#111] border-zinc-800 font-mono text-sm rounded-none" data-testid="city-filter">
              <MapPin className="w-4 h-4 mr-2 text-primary" />
              <SelectValue placeholder="CIUDAD" />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
              <SelectItem value="all" className="font-mono text-sm rounded-none">TODAS</SelectItem>
              {cities.map((city) => (
                <SelectItem key={city.city_id} value={city.city_id} className="font-mono text-sm rounded-none">
                  {city.name.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Period Filter */}
          <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[180px] bg-[#111] border-zinc-800 font-mono text-sm rounded-none" data-testid="period-filter">
              <Clock className="w-4 h-4 mr-2 text-primary" />
              <SelectValue placeholder="PERÍODO" />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
              {DATE_FILTERS.map((filter) => (
                <SelectItem key={filter.id} value={filter.id} className="font-mono text-sm rounded-none">
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort Order Toggle */}
          <Button
            variant="outline"
            onClick={toggleSortOrder}
            className="border-zinc-800 bg-[#111] font-mono text-sm rounded-none hover:bg-zinc-900"
            data-testid="sort-toggle"
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            {sortOrder === "asc" ? "MÁS CERCANOS" : "MÁS LEJANOS"}
          </Button>

          {/* Genre Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowGenres(!showGenres)}
            className={`border-zinc-800 font-mono text-sm rounded-none ${showGenres ? "bg-primary text-white" : "bg-[#111]"}`}
            data-testid="genre-toggle"
          >
            <Guitar className="w-4 h-4 mr-2" />
            GÉNEROS {selectedGenres.length > 0 && `(${selectedGenres.length})`}
          </Button>

          {/* Clear Filters */}
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="font-mono text-sm text-zinc-500 hover:text-white"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              LIMPIAR
            </Button>
          )}

          {/* View Mode Toggle */}
          <div className="flex bg-[#111] border border-zinc-800 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("list")}
              className={`rounded-none font-mono text-xs px-4 ${
                viewMode === "list" ? "bg-primary text-white" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-2" />
              LISTA
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("map")}
              className={`rounded-none font-mono text-xs px-4 ${
                viewMode === "map" ? "bg-primary text-white" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="view-map-btn"
            >
              <MapIcon className="w-4 h-4 mr-2" />
              MAPA
            </Button>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {EVENT_TAGS.map((tag) => (
            <button
              key={tag.id}
              onClick={() => toggleTag(tag.id)}
              className={`px-4 py-2 font-mono text-xs uppercase border transition-all ${
                selectedTags.includes(tag.id)
                  ? "bg-primary text-white border-primary"
                  : "border-zinc-800 text-zinc-400 hover:border-primary hover:text-primary"
              }`}
              data-testid={`tag-${tag.id}`}
            >
              <Tag className="w-3 h-3 inline mr-1" />
              {tag.label}
            </button>
          ))}
        </div>

        {/* Genres Panel */}
        {showGenres && (
          <div className="bg-[#111] border border-zinc-800 p-4 mb-6">
            <h3 className="font-mono text-xs text-zinc-500 mb-3">GÉNEROS MUSICALES</h3>
            <div className="space-y-4">
              {MUSIC_GENRES.map((category) => (
                <div key={category.category}>
                  <p className="font-mono text-xs text-primary mb-2">{category.category.toUpperCase()}</p>
                  <div className="flex flex-wrap gap-2">
                    {category.genres.map((genre) => (
                      <button
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`px-3 py-1 font-mono text-xs border transition-all ${
                          selectedGenres.includes(genre)
                            ? "bg-zinc-700 text-white border-zinc-600"
                            : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                        }`}
                        data-testid={`genre-${genre}`}
                      >
                        {genre}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Genre Filters */}
        {selectedGenres.length > 0 && !showGenres && (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedGenres.map((genre) => (
              <button
                key={genre}
                onClick={() => toggleGenre(genre)}
                className="px-3 py-1 font-mono text-xs bg-zinc-700 text-white border border-zinc-600 flex items-center gap-1"
              >
                {genre}
                <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}

        {/* Map View */}
        {viewMode === "map" && (
          <div className="mb-8">
            <div className="h-[500px] border border-zinc-800 overflow-hidden" data-testid="events-map">
              {(() => {
                const eventsWithLocation = events.filter(e => e.bar_latitude && e.bar_longitude);
                if (eventsWithLocation.length > 0) {
                  // Calculate center from events
                  const avgLat = eventsWithLocation.reduce((sum, e) => sum + e.bar_latitude, 0) / eventsWithLocation.length;
                  const avgLng = eventsWithLocation.reduce((sum, e) => sum + e.bar_longitude, 0) / eventsWithLocation.length;
                  
                  return (
                    <MapContainer
                      center={[avgLat, avgLng]}
                      zoom={DEFAULT_ZOOM}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom={true}
                    >
                      <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                      />
                      {eventsWithLocation.map((event) => {
                        const isPast = isBefore(new Date(event.event_date), new Date());
                        return (
                          <Marker 
                            key={event.event_id} 
                            position={[event.bar_latitude, event.bar_longitude]}
                            icon={isPast ? grayIcon : redIcon}
                          >
                            <Popup className="rock-popup">
                              <div className="min-w-[220px]">
                                <div className="text-xs text-gray-500 mb-1">
                                  {format(new Date(event.event_date), "EEEE d MMM · HH:mm", { locale: es })}H
                                  {isPast && <span className="ml-2 text-gray-400">(PASADO)</span>}
                                </div>
                                <h3 className="font-bold text-lg mb-1">{event.title}</h3>
                                <p className="text-sm text-gray-600 mb-2">📍 {event.bar_name}</p>
                                {event.price && (
                                  <p className="text-sm font-semibold text-red-600 mb-2">{event.price}</p>
                                )}
                                <Link
                                  to={`/events/${event.event_id}`}
                                  className="inline-block mt-2 text-sm font-bold text-red-600 hover:text-red-800"
                                >
                                  VER EVENTO →
                                </Link>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  );
                } else {
                  return (
                    <div className="h-full flex items-center justify-center bg-[#111]">
                      <div className="text-center">
                        <MapIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                        <p className="text-zinc-500 font-mono text-sm">
                          No hay eventos con ubicación en el mapa
                        </p>
                        <p className="text-zinc-600 font-mono text-xs mt-2">
                          Los eventos aparecerán cuando sus bares tengan coordenadas
                        </p>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
            
            {/* Event list below map for quick reference */}
            {events.filter(e => e.bar_latitude && e.bar_longitude).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {events.filter(e => e.bar_latitude && e.bar_longitude).slice(0, 10).map((event) => {
                  const isPast = isBefore(new Date(event.event_date), new Date());
                  return (
                    <Link
                      key={event.event_id}
                      to={`/events/${event.event_id}`}
                      className={`bg-[#111] border border-zinc-800 px-4 py-2 font-mono text-sm transition-colors ${
                        isPast ? "text-zinc-500 hover:text-zinc-300" : "text-zinc-300 hover:text-primary hover:border-primary"
                      }`}
                    >
                      <CalendarIcon className="w-3 h-3 inline mr-2 text-primary" />
                      {format(new Date(event.event_date), "d MMM", { locale: es })} - {event.title.substring(0, 25)}{event.title.length > 25 ? "..." : ""}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Events List */}
        {viewMode === "list" && (loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#111] border border-zinc-800 h-40 animate-pulse" />
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="space-y-4">
            {events.map((event) => {
              const isPastEvent = isBefore(new Date(event.event_date), new Date());
              
              return (
              <Link
                key={event.event_id}
                to={`/events/${event.event_id}`}
                className={`group card-ruido bg-[#111] border border-zinc-800 flex flex-col sm:flex-row overflow-hidden relative ${
                  isPastEvent ? "opacity-50 hover:opacity-75" : ""
                }`}
                data-testid={`event-item-${event.event_id}`}
              >
                {/* Past Event Badge */}
                {isPastEvent && (
                  <div className="absolute top-2 right-2 z-10 bg-zinc-800/90 text-zinc-400 font-mono text-xs px-2 py-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    PASADO
                  </div>
                )}
                
                {/* Date Column */}
                <div className={`p-4 sm:p-6 flex sm:flex-col items-center justify-center sm:w-32 flex-shrink-0 ${
                  isPastEvent ? "bg-zinc-700" : "bg-primary"
                }`}>
                  <span className="text-3xl sm:text-4xl font-black text-white">
                    {format(new Date(event.event_date), "d")}
                  </span>
                  <span className="text-sm font-mono text-white/80 ml-2 sm:ml-0 uppercase">
                    {format(new Date(event.event_date), "MMM", { locale: es })}
                  </span>
                  <span className={`text-sm font-mono ml-2 sm:ml-0 uppercase ${
                    isPastEvent ? "text-zinc-400" : "text-black"
                  }`}>
                    {format(new Date(event.event_date), "EEEE", { locale: es })}
                  </span>
                </div>

                {/* Image */}
                {event.image_url && (
                  <div className="w-full sm:w-48 h-32 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 p-4 sm:p-6 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-4 mb-2 text-zinc-500 font-mono text-xs">
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3 text-primary" />
                      {format(new Date(event.event_date), "HH:mm")}H
                      {event.end_date && ` - ${format(new Date(event.end_date), "HH:mm")}H`}
                    </span>
                    {event.bar_name && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.bar_name}
                        </span>
                      </>
                    )}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-primary transition-colors mb-2">
                    {event.title}
                  </h3>
                  
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {event.price && (
                      <span className="bg-zinc-800 px-3 py-1 font-mono text-sm text-white">
                        {event.price}
                      </span>
                    )}
                    {event.tags?.slice(0, 3).map((tag) => (
                      <span key={tag} className="bg-primary/20 text-primary text-xs font-mono px-2 py-1 uppercase">
                        {tag}
                      </span>
                    ))}
                    {event.music_genres?.slice(0, 2).map((genre) => (
                      <span key={genre} className="bg-zinc-800 text-zinc-300 text-xs font-mono px-2 py-1">
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Arrow */}
                <div className="hidden sm:flex items-center px-6 border-l border-zinc-800 group-hover:border-primary transition-colors">
                  <ArrowRight className="w-6 h-6 text-zinc-600 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
              );
            })}
          </div>
        ) : (
          <div className="bg-[#111] border border-zinc-800 p-12 text-center">
            <CalendarIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">NO HAY EVENTOS</h3>
            <p className="text-zinc-500 font-mono text-sm">
              {hasFilters 
                ? "No hay eventos con los filtros seleccionados" 
                : "Todavía no hay eventos programados"}
            </p>
            {hasFilters && (
              <Button
                onClick={clearFilters}
                className="mt-4 btn-ruido bg-primary text-white font-mono"
              >
                VER TODOS LOS EVENTOS
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
